import { useMemo, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import {
  DAY_LABELS, MONTH_LABELS, EVENT_COLORS,
  parseEventDate, startOfDay, addDays, startOfWeek, isSameDay,
  formatRange, formatFullDate, eventsOverlappingDay,
} from "@/lib/calendar";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight, Plus, Clock, Path, Prohibit } from "@phosphor-icons/react";
import { useBranding } from "@/contexts/BrandingContext";

// Shared calendar view used by both drivers (editable) and admin (read-only).
// Props:
//   driverId:     string | null  (null = current user)
//   readOnly:     boolean        (admin mode disables add/edit)
//   onAddPersonal: fn            (called with the selected date)
//   onAddUnavailability: fn
//   onEventClick: fn(event)
//   refreshKey:   any            (bump to force reload)
export default function CalendarView({
  driverId, readOnly = false, onAddPersonal, onAddUnavailability, onEventClick, refreshKey,
}) {
  const branding = useBranding();
  const [view, setView] = useState("week"); // "week" | "month"
  const [anchor, setAnchor] = useState(startOfDay(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const [rangeStart, rangeEnd] = useMemo(() => {
    if (view === "week") {
      const s = startOfWeek(anchor);
      return [s, addDays(s, 7)];
    }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return [gridStart, addDays(gridStart, 42)];
  }, [view, anchor]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start: rangeStart.toISOString(),
        end: rangeEnd.toISOString(),
      });
      if (driverId) params.set("driver_id", driverId);
      const { data } = await api.get(`/calendar/events?${params.toString()}`);
      setEvents(Array.isArray(data) ? data : []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [driverId, rangeStart, rangeEnd]);

  useEffect(() => { fetchEvents(); }, [fetchEvents, refreshKey]);

  const goToday = () => setAnchor(startOfDay(new Date()));
  const prev = () => setAnchor(view === "week" ? addDays(anchor, -7)
    : new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
  const next = () => setAnchor(view === "week" ? addDays(anchor, 7)
    : new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));

  const headerLabel = view === "week"
    ? `${rangeStart.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} – ${addDays(rangeStart, 6).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}`
    : `${MONTH_LABELS[anchor.getMonth()]} ${anchor.getFullYear()}`;

  return (
    <div data-testid="calendar-view" className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Button data-testid="cal-prev" variant="outline" size="icon" onClick={prev}
            className="h-9 w-9 border-zinc-700 text-zinc-300 hover:bg-zinc-800"><CaretLeft size={16} /></Button>
          <Button data-testid="cal-today" variant="outline" onClick={goToday}
            className="h-9 px-3 border-zinc-700 text-zinc-300 hover:bg-zinc-800">Aujourd'hui</Button>
          <Button data-testid="cal-next" variant="outline" size="icon" onClick={next}
            className="h-9 w-9 border-zinc-700 text-zinc-300 hover:bg-zinc-800"><CaretRight size={16} /></Button>
        </div>
        <div className="text-sm font-medium text-white flex-1 text-center min-w-0 truncate">{headerLabel}</div>
        <div className="flex rounded-md border border-zinc-700 overflow-hidden">
          <button data-testid="cal-view-week" onClick={() => setView("week")}
            className={`px-3 h-9 text-xs font-medium transition-colors ${view === "week" ? "bg-amber-500 text-black" : "bg-transparent text-zinc-300 hover:bg-zinc-800"}`}>Semaine</button>
          <button data-testid="cal-view-month" onClick={() => setView("month")}
            className={`px-3 h-9 text-xs font-medium transition-colors ${view === "month" ? "bg-amber-500 text-black" : "bg-transparent text-zinc-300 hover:bg-zinc-800"}`}>Mois</button>
        </div>
      </div>

      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          <Button data-testid="cal-add-personal" onClick={() => onAddPersonal?.(anchor)}
            className="bg-sky-500 hover:bg-sky-600 text-white h-9 px-3 text-xs font-semibold">
            <Plus size={14} className="mr-1.5" /> Course personnelle
          </Button>
          <Button data-testid="cal-add-unavailable" onClick={() => onAddUnavailability?.(anchor)}
            className="bg-rose-500 hover:bg-rose-600 text-white h-9 px-3 text-xs font-semibold">
            <Prohibit size={14} className="mr-1.5" /> Indisponibilité
          </Button>
        </div>
      )}

      <LegendRow />

      {view === "week"
        ? <WeekGrid start={rangeStart} events={events} loading={loading} onEventClick={onEventClick} />
        : <MonthGrid anchorMonth={anchor.getMonth()} start={rangeStart} events={events} loading={loading} onEventClick={onEventClick} />}
    </div>
  );
}

function LegendRow() {
  return (
    <div className="flex flex-wrap gap-3 text-xs text-zinc-400">
      {Object.entries(EVENT_COLORS).map(([k, c]) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className={`inline-block w-2.5 h-2.5 rounded-sm ${c.dot}`} />{c.label}
        </span>
      ))}
    </div>
  );
}

function WeekGrid({ start, events, loading, onEventClick }) {
  const today = startOfDay(new Date());
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div data-testid="cal-week-grid" className="grid grid-cols-1 sm:grid-cols-7 gap-2">
      {days.map((d, i) => {
        const dayEvents = eventsOverlappingDay(events, d);
        const isToday = isSameDay(d, today);
        return (
          <div key={i} data-testid={`cal-day-${i}`}
            className={`rounded-md border bg-[#121212] p-2 min-h-[120px] ${isToday ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-zinc-800"}`}>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-xs text-zinc-500 uppercase tracking-wider">{DAY_LABELS[i]}</span>
              <span className={`text-sm font-semibold ${isToday ? "text-amber-400" : "text-white"}`}>{d.getDate()}</span>
            </div>
            <div className="space-y-1.5">
              {loading && dayEvents.length === 0 && <div className="h-6 rounded bg-zinc-800/50 animate-pulse" />}
              {!loading && dayEvents.length === 0 && <p className="text-[11px] text-zinc-600">—</p>}
              {dayEvents.map((ev) => <EventPill key={ev.id} ev={ev} onClick={() => onEventClick?.(ev)} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthGrid({ anchorMonth, start, events, loading, onEventClick }) {
  const today = startOfDay(new Date());
  const weeks = Array.from({ length: 6 }, (_, w) =>
    Array.from({ length: 7 }, (_, d) => addDays(start, w * 7 + d)));
  return (
    <div data-testid="cal-month-grid" className="space-y-1">
      <div className="grid grid-cols-7 gap-1 text-xs text-zinc-500 uppercase tracking-wider px-1">
        {DAY_LABELS.map((l) => <div key={l} className="text-center py-1">{l}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {weeks.flat().map((d, idx) => {
          const dayEvents = eventsOverlappingDay(events, d);
          const inMonth = d.getMonth() === anchorMonth;
          const isToday = isSameDay(d, today);
          return (
            <div key={idx} data-testid={`cal-month-cell-${idx}`}
              className={`rounded border p-1 min-h-[70px] text-left ${inMonth ? "bg-[#121212] border-zinc-800" : "bg-[#0a0a0a] border-zinc-900/50"} ${isToday ? "ring-1 ring-amber-500/60 border-amber-500/60" : ""}`}>
              <div className={`text-[11px] font-medium ${isToday ? "text-amber-400" : inMonth ? "text-zinc-300" : "text-zinc-600"}`}>{d.getDate()}</div>
              <div className="mt-0.5 space-y-0.5">
                {loading && dayEvents.length === 0 && <div className="h-2.5 rounded bg-zinc-800/50 animate-pulse" />}
                {dayEvents.slice(0, 3).map((ev) => <MonthDot key={ev.id} ev={ev} onClick={() => onEventClick?.(ev)} />)}
                {dayEvents.length > 3 && <div className="text-[10px] text-zinc-500">+{dayEvents.length - 3}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventPill({ ev, onClick }) {
  const color = EVENT_COLORS[ev.event_type] || EVENT_COLORS.personal_ride;
  const Icon = ev.event_type === "sdrive_ride" ? Path
             : ev.event_type === "unavailability" ? Prohibit : Clock;
  const s = parseEventDate(ev.start_datetime);
  const title = ev.event_type === "sdrive_ride" ? (ev.route_label || "Course")
    : ev.event_type === "personal_ride" ? (ev.client_name || ev.pickup_location || "Course personnelle")
    : ev.event_type === "driver_personal_ride" ? (ev.title || ev.client_name || "Course perso")
    : (ev.notes || "Indisponible");
  return (
    <button data-testid={`cal-event-${ev.id}`} onClick={onClick}
      className={`w-full text-left rounded px-1.5 py-1 border ${color.bg} ${color.border} ${color.text} hover:brightness-125 transition-all`}>
      <div className="flex items-center gap-1 text-[11px] font-semibold">
        <Icon size={11} weight="bold" />
        <span>{s ? s.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
      </div>
      <div className="text-[11px] truncate leading-tight">{title}</div>
    </button>
  );
}

function MonthDot({ ev, onClick }) {
  const branding = useBranding();
  const color = EVENT_COLORS[ev.event_type] || EVENT_COLORS.personal_ride;
  return (
    <button onClick={onClick} data-testid={`cal-event-dot-${ev.id}`}
      className={`w-full text-left rounded px-1 py-0.5 text-[10px] truncate ${color.bg} ${color.text} border ${color.border}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${color.dot} mr-1`} />
      {ev.event_type === "sdrive_ride" ? (ev.route_label || branding.companyName)
        : ev.event_type === "personal_ride" ? (ev.client_name || "Perso")
        : ev.event_type === "driver_personal_ride" ? (ev.client_name || "Perso")
        : "Indispo"}
    </button>
  );
}

export function formatEventSummary(ev) {
  return `${formatRange(ev.start_datetime, ev.end_datetime)}`;
}

export { formatFullDate };
